import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AppState } from "../root";
import { ToastService } from "../../services/toast/toast.service";
import { IntegrationActions } from "./integrations.actions";
import { withLatestFrom, switchMap, of, map, catchError } from "rxjs";
import { ToastType } from "../../models";
import { selectUser } from "../user/user.selectors";
import { IntegrationService } from "../../services/integrations/integrations.service";

@Injectable()
export class IntegrationsEffects {
    private actions$ = inject(Actions);
    private store$ = inject(Store<AppState>);
    private integrationService = inject(IntegrationService)
    private toastService = inject(ToastService);

    loadUserIntegrations$ = createEffect(() => this.actions$.pipe(
        ofType(IntegrationActions.loadUsersIntegrations),
        withLatestFrom(this.store$.select(selectUser)),
        switchMap(([action, user]) => {
            if (user == null) {
                this.toastService.showToast("Failed to load integrations", ToastType.ERROR)
                return of(IntegrationActions.loadIntegrationsFailure({ error: Error("Invalid user") }))
            }
            return of(IntegrationActions.loadIntegrations({ organisationId: user.organisation_id }))
        })
    ));

    loadIntegrations$ = createEffect(() => this.actions$.pipe(
        ofType(IntegrationActions.loadIntegrations),
        switchMap((action) => {
            return this.integrationService.getAllIntegrations(action.organisationId).pipe(
                map(e => {
                    return IntegrationActions.loadIntegrationsSuccess({ integrations: e })
                }),
                catchError(error => of(IntegrationActions.loadIntegrationsFailure({ error })))
            )
        })
    ));

    loadBlueprints$ = createEffect(() => this.actions$.pipe(
        ofType(IntegrationActions.loadBlueprints),
        switchMap((action) => {
            return this.integrationService.getAllBlueprints().pipe(
                map(e => {
                    return IntegrationActions.loadBlueprintsSuccess({ blueprints: e })
                }),
                catchError(error => of(IntegrationActions.loadBlueprintsFailure({ error })))
            )
        })
    ));

    deleteIntegration$ = createEffect(() => this.actions$.pipe(
        ofType(IntegrationActions.deleteIntegration),
        withLatestFrom(this.store$.select(selectUser)),
        switchMap(([action, user]) => {
            if (user == null) {
                this.toastService.showToast("Failed to delete integration", ToastType.ERROR)
                return of(IntegrationActions.deleteIntegrationFailure({ integration: action.integration, error: Error("Invalid user") }))
            }
            return this.integrationService.deleteIntegration(user.organisation_id, action.integration).pipe(
                map(_ => {
                    this.toastService.showToast("Deleted integration successfully", ToastType.SUCCESS)
                    return IntegrationActions.deleteIntegrationSuccess({ integration: action.integration })
                }),
                catchError(error => {
                    this.toastService.showToast("Failed to delete integration", ToastType.ERROR)
                    return of(IntegrationActions.deleteIntegrationFailure({ integration: action.integration, error }))
                })
            )
        })
    ));
}